import { Col, Container, Loading, Row, styled } from '@teko/ds-desktop';
import { trackCustomEvent } from '@teko/tracking';
import { isEmptyObject } from '@teko/utils';
import { useHamper } from 'contexts';
import { useMemo, useState } from 'react';
import { IHamperItemData } from 'types';
import { useTenantConfig } from '../../../../contexts/GlobalDataContext';
import {
  HamperItem,
  HamperModal,
  HamperSummary,
  HamperTabs,
} from './hamperComponent';

export const CustomHamper = () => {
  const [selectedBomItem, setSelectedBomItem] =
    useState<NonNullable<typeof bomMapping>[string]>();

  const { bomMapping } = useTenantConfig();
  const {
    loading,
    hamperData,
    changeActiveConfig,
    updateCurrentConfig,
    currentHamperConfig,
    hamperDetailProducts,
    increaseHamperItemQuantity,
    decreaseHamperItemQuantity,
    removeHamperItem,
    setHamperItemQuantity,
  } = useHamper();

  const bomMappingKeys = useMemo(() => {
    return !!bomMapping && !isEmptyObject(bomMapping)
      ? Object.keys(bomMapping)
      : [];
  }, [bomMapping]);

  const onClickBomItem = (
    hamperItem: NonNullable<typeof bomMapping>[string],
  ) => {
    setSelectedBomItem(hamperItem);
  };

  if (isEmptyObject(bomMapping) || !bomMapping) {
    return null;
  }

  return (
    <Container padding="s0" width="100%">
      <Loading isLoading={loading}>
        <Row marginTop="s16" justify="space-between">
          <Col width="66%" id="hamper-item-block">
            <HamperTabs
              lsActiveTab={hamperData.activeIndex}
              onClickTab={(index) => {
                changeActiveConfig(index);
                trackCustomEvent('changeHamperConfig', {
                  action: 'click',
                  category: 'custom_hamper',
                  value: index + 1,
                });
              }}
              onResetConfig={() => updateCurrentConfig({})}
            />
            <Col marginTop="s32">
              {bomMappingKeys.map((customHamper) => {
                const selectedHamper =
                  (currentHamperConfig[customHamper] as IHamperItemData[]) ||
                  [];
                const hampers = hamperDetailProducts.products
                  .filter((hamper) => {
                    return selectedHamper
                      .map((p) => p.sku)
                      .includes(hamper.sku);
                  })
                  .map((p) => {
                    return {
                      ...p,
                      quantity:
                        selectedHamper.find((h) => h.sku === p.sku)?.quantity ||
                        1,
                    };
                  });
                return (
                  <StyledHamperItem
                    key={bomMapping?.[customHamper]?.type}
                    title={bomMapping?.[customHamper]?.name}
                    icon={bomMapping[customHamper].icon}
                    products={
                      !!hampers && !!Array.isArray(hampers) ? hampers : []
                    }
                    onIncrease={(sku: string) =>
                      increaseHamperItemQuantity(customHamper, sku)
                    }
                    onDecrease={(sku, quantity) => {
                      quantity === 1
                        ? removeHamperItem(customHamper, sku)
                        : decreaseHamperItemQuantity(customHamper, sku);
                    }}
                    setQuantity={(value, sku) => {
                      setHamperItemQuantity(customHamper, value, sku);
                    }}
                    onClick={() => onClickBomItem(bomMapping?.[customHamper])}
                  />
                );
              })}
            </Col>
          </Col>
          <Col width="33%">
            <HamperSummary />
          </Col>
        </Row>

        <HamperModal
          collectionSlug={selectedBomItem?.slug || ''}
          visible={!!selectedBomItem}
          onClose={() => setSelectedBomItem(undefined)}
          hamperItemKey={selectedBomItem?.type || ''}
        />
      </Loading>
    </Container>
  );
};

const StyledHamperItem = styled(HamperItem)`
  margin-bottom: 1rem;
`;
